import { ReactNode, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Link,
  Tab,
  Tabs,
} from '@mui/material';

export const chromeExtensionUrl =
  'https://chromewebstore.google.com/detail/dotstorming/pekgjfdidbcedcedcakipkbgofdffjog';

type HelpEmbed = {
  key: 'board' | 'whiteboard' | 'quick-sort' | 'chrome-extension';
  label: string;
  src?: string;
  title?: string;
  url?: string;
  content?: ReactNode;
};

const helpEmbeds: HelpEmbed[] = [
  {
    key: 'board',
    label: 'How to use boards',
    src: 'https://demo.arcade.software/TsadkDAcXKqgTpkMMHqq?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    title: 'Create and Collaborate on Interactive Boards',
  },
  {
    key: 'whiteboard',
    label: 'How to use whiteboards',
    src: 'https://demo.arcade.software/uNO3qIViGDH0ZG6KN7Xm?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    title: 'Create and Share a Collaborative Whiteboard',
  },
  {
    key: 'quick-sort',
    label: 'How to use Quick Sorts',
    src: 'https://demo.arcade.software/Eo7etZ5kzysbn93zOawJ?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    title: 'How to use Quick Sorts',
  },
  {
    key: 'chrome-extension',
    label: 'Chrome extension',
    src: 'https://demo.arcade.software/XzBOvD7jbKXFolebrIBd?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    title: 'Chrome extension installation walkthrough',
    url: chromeExtensionUrl,
  },
];

export type HelpDialogTabKey = HelpEmbed['key'];

type HelpDialogProps = {
  open: boolean;
  onClose: () => void;
  initialTabKey?: HelpDialogTabKey;
};

export const HelpDialog = ({
  open,
  onClose,
  initialTabKey = 'board',
}: HelpDialogProps) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const initialTabIndex = useMemo(
    () =>
      Math.max(
        helpEmbeds.findIndex((embed) => embed.key === initialTabKey),
        0
      ),
    [initialTabKey]
  );

  useEffect(() => {
    if (open) {
      setSelectedTab(initialTabIndex);
    }
  }, [initialTabIndex, open]);

  const handleTabChange = (_event: SyntheticEvent, value: number) => {
    setSelectedTab(value);
  };

  const selectedEmbed = helpEmbeds[selectedTab] ?? helpEmbeds[0];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <Tabs value={selectedTab} onChange={handleTabChange} variant="fullWidth">
        {helpEmbeds.map((embed) => (
          <Tab key={embed.key} label={embed.label} />
        ))}
      </Tabs>
      <DialogContent sx={{ height: '100vh', pt: selectedEmbed.url ? 2 : 3 }}>
        {selectedEmbed.url ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              mb: 2,
              flexWrap: 'wrap',
            }}
          >
            <Link href={selectedEmbed.url} target="_blank" rel="noreferrer" underline="hover">
              {selectedEmbed.url}
            </Link>
          </Box>
        ) : null}
        {selectedEmbed.content ? (
          selectedEmbed.content
        ) : (
          <iframe
            src={selectedEmbed.src ?? ''}
            title={selectedEmbed.title ?? ''}
            frameBorder="0"
            loading="lazy"
            allowFullScreen
            allow="clipboard-write"
            style={{
              width: '100%',
              height: '100%',
              colorScheme: 'light',
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
